import React, {useEffect, useState} from "react";
import {getMemoList, removeMemos, saveMemo, updateMemo} from "../../api/memo/MemoService";
import PaginationCustom from "../table/PaginationCustom";
import {handleActiveError, handleActiveToast} from "../sonner/ToastBasic";
import {useSelector} from "react-redux";
import {isMobile} from "../commonmethod/CommonMethod";
import {DetailPanel} from "../detailLayout";
import MemoExpandModal, {isLongMemo} from "./MemoExpandModal";
import "../detailLayout/detailListLayout.css";
import "./memo.css";

function formatMemoDate(value) {
    if (!value) return "";
    const text = String(value);
    return text.includes("T") ? text.replace("T", " ").slice(0, 16) : text.slice(0, 16);
}

/**
 * 작업 기록 — 타임라인형 진행 내역
 */
function MemoList({entityId, entityName, title = "메모", embedded = false}) {
    const userId = useSelector((state) => state.auth.user.id);
    const [data, setData] = useState([]);
    const [draft, setDraft] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editContent, setEditContent] = useState("");
    const [saving, setSaving] = useState(false);
    const [expandModal, setExpandModal] = useState(null);

    const [search] = useState({entityId, entityName});
    const [pageable, setPageable] = useState({
        sort: "createdAt",
        desc: true,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        page: 0,
    });

    useEffect(() => {
        getList();
    }, [pageable.page]);

    useEffect(() => {
        if (!editingId || expandModal) return undefined;

        function handleMouseDown(e) {
            const editingEntry = document.querySelector(".memo-log-entry.is-editing");
            if (!editingEntry || editingEntry.contains(e.target)) return;
            setEditingId(null);
        }

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [editingId, expandModal]);

    async function getList() {
        try {
            const response = await getMemoList(search, pageable);
            if (response.status === 200) {
                setData(response.data.content);
                setPageable((prev) => ({
                    ...prev,
                    totalElements: response.data.totalElements,
                    totalPages: response.data.totalPages,
                }));
            }
        } catch (error) {
            /* ignore */
        }
    }

    const handleDelete = async (row, e) => {
        e.stopPropagation();
        if (!window.confirm("이 메모를 삭제하시겠습니까?")) return;

        const response = await removeMemos({ids: [row.id]});
        if (response.status === 200) {
            handleActiveToast({message: "삭제되었습니다."});
            if (editingId === row.id) setEditingId(null);
            await getList();
        } else if (response.status === 403) {
            handleActiveError({message: "권한이 없습니다."});
        }
    };

    const handleCreate = async () => {
        const content = draft.trim();
        if (!content) return;
        setSaving(true);
        try {
            const response = await saveMemo({entityId, entityName, content});
            if (response.status === 200) {
                handleActiveToast({message: "메모가 추가되었습니다."});
                setDraft("");
                await getList();
            } else if (response.status === 403) {
                handleActiveError({message: "권한이 없습니다."});
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDraftKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleCreate();
        }
    };

    const startEdit = (row, e) => {
        if (e.target.closest(".memo-log-entry-delete")) return;
        if (userId !== row.createdBy?.id) return;
        setEditingId(row.id);
        setEditContent(row.content ?? "");
    };

    const handleUpdate = async (row, contentOverride) => {
        const content = (contentOverride ?? editContent).trim();
        if (!content) return;
        const response = await updateMemo({...row, content});
        if (response.status === 200) {
            handleActiveToast({message: "수정되었습니다."});
            setEditingId(null);
            setExpandModal(null);
            await getList();
        } else if (response.status === 403) {
            handleActiveError({message: "권한이 없습니다."});
        }
    };

    const openExpandView = (row, e) => {
        e.stopPropagation();
        setExpandModal({mode: "view", row, content: row.content ?? ""});
    };

    const openExpandEdit = (row, e) => {
        e?.stopPropagation?.();
        setExpandModal({mode: "edit", row, content: editContent});
    };

    if (isMobile) {
        return null;
    }

    const body = (
        <div className="memo-panel-body">
            <div className="memo-log-composer">
                <textarea
                    placeholder="새 메모..."
                    value={draft}
                    rows={3}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleDraftKeyDown}
                />
                <div className="memo-log-composer-foot">
                    <span className="memo-log-composer-hint">Ctrl+Enter</span>
                    <button
                        type="button"
                        className="memo-btn memo-btn--primary"
                        onClick={handleCreate}
                        disabled={saving || !draft.trim()}
                    >
                        추가
                    </button>
                </div>
            </div>

            <div className="memo-board-grid">
                {data.length === 0 ? (
                    <p className="memo-log-empty">아직 메모가 없습니다.</p>
                ) : (
                    data.map((row, index) => {
                        const isEditing = editingId === row.id;
                        const canEdit = userId === row.createdBy?.id;
                        const isLast = index === data.length - 1;

                        return (
                            <article
                                key={row.id}
                                className={[
                                    "memo-log-entry",
                                    isEditing ? "is-editing" : "",
                                    isLast ? "is-last" : "",
                                ].join(" ")}
                                onClick={(e) => !isEditing && startEdit(row, e)}
                            >
                                <div className="memo-log-entry-rail" aria-hidden="true">
                                    <span className="memo-log-entry-dot" />
                                </div>

                                <div className="memo-log-entry-body">
                                    <header className="memo-log-entry-head">
                                        <div className="memo-log-entry-meta">
                                            <span className="memo-log-entry-author">
                                                {row.createdBy?.name ?? "—"}
                                            </span>
                                            <time className="memo-log-entry-date">
                                                {formatMemoDate(row.createdAt)}
                                            </time>
                                        </div>
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="memo-log-entry-delete"
                                                onClick={(e) => handleDelete(row, e)}
                                                aria-label="삭제"
                                            >
                                                삭제
                                            </button>
                                        )}
                                    </header>

                                    {isEditing && canEdit ? (
                                        <div
                                            className="memo-log-entry-edit"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <textarea
                                                value={editContent}
                                                autoFocus
                                                onChange={(e) => setEditContent(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Escape") setEditingId(null);
                                                }}
                                            />
                                            <div className="memo-log-entry-actions">
                                                <button
                                                    type="button"
                                                    className="memo-btn memo-btn--text"
                                                    onClick={(e) => openExpandEdit(row, e)}
                                                >
                                                    전체 보기
                                                </button>
                                                <button
                                                    type="button"
                                                    className="memo-btn memo-btn--ghost"
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    취소
                                                </button>
                                                <button
                                                    type="button"
                                                    className="memo-btn memo-btn--primary"
                                                    onClick={() => handleUpdate(row)}
                                                >
                                                    저장
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="memo-log-entry-content">{row.content}</div>
                                            {isLongMemo(row.content) && (
                                                <button
                                                    type="button"
                                                    className="memo-btn memo-btn--text"
                                                    onClick={(e) => openExpandView(row, e)}
                                                >
                                                    전체 보기
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </article>
                        );
                    })
                )}
            </div>

            {data.length > 0 && pageable.totalPages > 1 && (
                <div className="memo-pagination">
                    <PaginationCustom paged={{pageable, setPageable}} />
                </div>
            )}


            {/*메모이력 : 현재는 굳이 필요 없어 보여서 주석처리함*/}
        {/*    <button*/}
        {/*        type="button"*/}
        {/*        className={`memo-history-toggle${historyOpen ? " is-open" : ""}`}*/}
        {/*        onClick={() => setHistoryOpen((v) => !v)}*/}
        {/*        aria-expanded={historyOpen}*/}
        {/*    >*/}
        {/*        <span className="memo-history-chevron">▶</span>*/}
        {/*        변경 이력*/}
        {/*    </button>*/}

        {/*    {historyOpen && (*/}
        {/*        <div className="memo-history-panel">*/}
        {/*            <MemoHistory entityId={entityId} entityName={entityName} />*/}
        {/*        </div>*/}
        {/*    )}*/}
        </div>
    );

    const modal = expandModal ? (
        <MemoExpandModal
            open={Boolean(expandModal)}
            onOpenChange={(open) => !open && setExpandModal(null)}
            title={expandModal.mode === "edit" ? "메모 수정" : "메모 전체 보기"}
            content={expandModal.mode === "edit" ? editContent : expandModal.content}
            readOnly={expandModal.mode === "view"}
            meta={
                expandModal.mode === "view" && expandModal.row
                    ? {
                          author: expandModal.row.createdBy?.name,
                          date: formatMemoDate(expandModal.row.createdAt),
                      }
                    : undefined
            }
            onSave={
                expandModal.mode === "edit" && expandModal.row
                    ? (text) => {
                          setEditContent(text);
                          handleUpdate(expandModal.row, text);
                      }
                    : undefined
            }
        />
    ) : null;

    if (embedded) {
        return (
            <>
                {body}
                {modal}
            </>
        );
    }

    return (
        <DetailPanel title={title}>
            {body}
            {modal}
        </DetailPanel>
    );
}

export {MemoList};
